import React from "react";
import { ConfidencePill, DataState, IcClock, IcEdit, IcEscrow, IcKey, IcMail, IcRefresh, IcSearch, IcSend, IcX, StatusPill } from "./shared.jsx";
import { FooterNav, StepHeader } from "./steps_early.jsx";
// ============ ESCROW TRANSFERS ============
// Pending / in-escrow contracts come from BoldTrail BackOffice (Brokermint).
// The brokerage holds one org-level Brokermint API key (see backend/BROKERMINT.md);
// we filter transactions to this agent by their Brokermint user id and to
// status "pending" (under contract / in escrow). Each one needs My Home Group's
// "Open Escrow Authorization to Transfer to New Brokerage" executed by the
// Buyer, Seller, MHG Broker, and the Receiving Broker (EveryPlace).
const { useState: _e_uS, useRef: _e_uR, useEffect: _e_uE, useCallback: _e_uC } = React;

const MHG = "My Home Group LLC";
const RECEIVING_BROKER = "EveryPlace";

function StepEscrow({ onNext, onBack, escrows, setEscrows, agent, openSign, loading, error, onReload }) {
  const [query, setQuery] = _e_uS("");
  const [selected, setSelected] = _e_uS(null);
  const [syncing, setSyncing] = _e_uS(false);
  const [searchResults, setSearchResults] = _e_uS([]);
  const [searching, setSearching] = _e_uS(false);
  const searchTimer = _e_uR(null);

  const allTransferred = escrows.length > 0 && escrows.every(e => e.transferStatus === "transferred");
  const anyInFlight = escrows.some(e => ["sent_parties", "countersign"].includes(e.transferStatus));

  async function resync() {
    setSyncing(true);
    try { await onReload?.(); }
    finally { setSyncing(false); }
  }

  const handleSearchInput = _e_uC((val) => {
    setQuery(val);
    clearTimeout(searchTimer.current);
    if (val.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/v1/escrows/search?q=${encodeURIComponent(val.trim())}`);
        const data = await r.json();
        // Filter out escrows already in the main list
        const existing = new Set(escrows.map(e => e.id));
        setSearchResults((data.escrows || []).filter(e => !existing.has(e.id)));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 400);
  }, [escrows]);

  function addFromSearch(e) {
    setEscrows(curr => curr.some(x => x.id === e.id) ? curr : [...curr, e]);
    setSearchResults([]);
    setQuery("");
    setSelected(e.id);
  }

  return (
    <div style={{maxWidth: 1100}}>
      <StepHeader eyebrow="Step 07 / 09" title="Transfer your pending escrows."
        subtitle={<>Your pending listings are auto-detected from the MLS. For buy-side escrows, search by MLS# or address below. Each open escrow needs My Home Group's transfer authorization — signed by the buyer or seller, then {RECEIVING_BROKER}.</>}/>

      <DataState loading={loading} error={error} onReload={onReload} noun="escrows"/>

      {/* Buy-side search bar */}
      <div style={{marginBottom: 8, padding: "16px 20px", background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", borderRadius: 2}}>
        <div style={{display: "flex", gap: 14, alignItems: "center"}}>
          <IcSearch size={18}/>
          <input className="ep-input" style={{border: 0, background: "transparent", padding: 0, fontSize: 15, flex: 1}}
            placeholder="Find buy-side escrow — search by MLS#, address, buyer, or seller"
            value={query} onChange={e => handleSearchInput(e.target.value)}/>
          {searching && <span style={{fontSize: 12, color: "var(--ep-fg-muted)", whiteSpace: "nowrap"}}>Searching…</span>}
          <button className="ep-btn ghost sm" onClick={resync} disabled={syncing}>
            <IcRefresh size={12}/> {syncing ? "Syncing…" : "Re-sync"}
          </button>
        </div>
        {searchResults.length > 0 && (
          <div style={{marginTop: 12, display: "flex", flexDirection: "column", gap: 8}}>
            <div className="ep-eyebrow" style={{color: "var(--ep-sunset)", marginBottom: 4}}>Search results — click to add &amp; review</div>
            {searchResults.map(e => (
              <div key={e.id} style={{display: "flex", alignItems: "center", gap: 14, padding: "10px 14px", background: "rgba(204,207,129,0.12)", border: "1px solid var(--ep-avocado)", borderRadius: 2, cursor: "pointer"}}
                onClick={() => addFromSearch(e)}>
                <IcEscrow size={20}/>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={{fontWeight: 600, fontSize: 14}}>{e.address}</div>
                  <div style={{fontSize: 12, color: "var(--ep-fg-muted)"}}>
                    {e.city && <span>{e.city} · </span>}
                    Escrow #{e.escrowNumber || "—"} · Close {e.closeDate}
                    {" · "}Buyer: {e.buyer?.name || "—"} · Seller: {e.seller?.name || "—"}
                  </div>
                </div>
                <button className="ep-btn secondary sm" onClick={(ev) => { ev.stopPropagation(); addFromSearch(e); }}>
                  Review &amp; Send
                </button>
              </div>
            ))}
          </div>
        )}
        {query.trim().length >= 2 && !searching && searchResults.length === 0 && (
          <div style={{marginTop: 10, fontSize: 13, color: "var(--ep-fg-muted)"}}>No matching pending escrows found (≥14 days out). Escrow cache updates every 3 hours.</div>
        )}
      </div>

      {/* Counts */}
      <div className="ep-strip-mobile" style={{display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, marginBottom: 32, background: "var(--ep-border)", border: "1px solid var(--ep-border)"}}>
        {[
          {n: escrows.length, l: "Pending Escrows"},
          {n: escrows.filter(e => e.transferStatus === "draft").length, l: "Need Review"},
          {n: escrows.filter(e => ["sent_parties","countersign"].includes(e.transferStatus)).length, l: "Out for Signature"},
          {n: escrows.filter(e => e.transferStatus === "transferred").length, l: "Transferred"},
        ].map((s,i) => (
          <div key={i} style={{background: "var(--ep-bg-elevated)", padding: "20px 22px"}}>
            <div style={{font: "300 42px/1 var(--ep-font-display)", color: "var(--ep-origin)"}}>{s.n}</div>
            <div className="ep-caption" style={{marginTop: 6}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Sell-side escrow rows (auto-detected from MLS) */}
      <div className="vstack" style={{gap: 14, marginBottom: 40}}>
        {escrows.length === 0 && !loading && !error && (
          <div style={{padding: 40, textAlign: "center", color: "var(--ep-fg-muted)", border: "1px dashed var(--ep-border)", background: "var(--ep-bg-elevated)"}}>
            No pending sell-side escrows detected from your MLS listings. Search above for buy-side escrows.
          </div>
        )}
        {escrows.map(e => (
          <EscrowRow key={e.id} escrow={e} agent={agent}
            onEdit={() => setSelected(e.id)}/>
        ))}
      </div>

      {selected && (
        <EscrowDetailModal escrow={escrows.find(e => e.id === selected)} agent={agent}
          onClose={() => setSelected(null)}
          onSave={(updated) => { setEscrows(escrows.map(x => x.id === updated.id ? updated : x)); setSelected(null); }}
          onSend={(updated) => {
            setEscrows(escrows.map(x => x.id === updated.id ? updated : x));
            setSelected(null);
            launchSign(updated, setEscrows);
          }}/>
      )}

      <FooterNav onBack={onBack} onNext={onNext}
        nextDisabled={!allTransferred && !window.__allowSkipEscrows}
        nextLabel={allTransferred ? "Continue to Buyer Agreements"
          : (anyInFlight ? "Waiting on signatures…" : "Send escrows for signature first")}/>

      {!allTransferred && (
        <div style={{textAlign: "right", marginTop: 12}}>
          <span className="ep-link" style={{fontSize: 12, color: "var(--ep-fg-muted)"}}
            onClick={() => { window.__allowSkipEscrows = true; onNext(); }}>
            Skip for now (handle escrow transfers later)
          </span>
        </div>
      )}
    </div>
  );
}

async function launchSign(e, setEscrows) {
  setEscrows(curr => curr.map(x => x.id === e.id
    ? { ...x, transferStatus: "sent_parties", transferError: null } : x));
  try {
    const resp = await fetch(`/api/v1/escrows/${e.bmId}/transfer-authorization`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({
        represents:            e.represents,
        escrowNumber:          e.escrowNumber,
        escrowCompany:         e.escrowCompany,
        transactionOwnerEmail: e.transactionOwnerEmail || "",
        buyer:                 e.buyer,
        seller:                e.seller,
        buyer2:                e.buyer2  || null,
        seller2:               e.seller2 || null,
      }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const msg = json?.error || json?.message || `HTTP ${resp.status} — check browser console`;
      console.error("[escrow-transfer] failed:", resp.status, json);
      setEscrows(curr => curr.map(x => x.id === e.id
        ? { ...x, transferStatus: "draft", transferError: msg } : x));
      return;
    }
    setEscrows(curr => curr.map(x => x.id === e.id
      ? { ...x, transferStatus: "countersign", envelopeId: json.envelopeId, transferError: null } : x));
  } catch (err) {
    console.error("[escrow-transfer] network error:", err);
    setEscrows(curr => curr.map(x => x.id === e.id
      ? { ...x, transferStatus: "draft", transferError: `Network error: ${err.message}` } : x));
  }
}

function EscrowRow({ escrow, agent, onEdit }) {
  const e = escrow;
  return (
    <div className="listing-card ep-stack-mobile" style={{gridTemplateColumns: "auto 1fr auto"}}>
      <div style={{width: 64, height: 64, borderRadius: 2, background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0}}>
        <IcEscrow size={26}/>
      </div>
      <div style={{display: "flex", flexDirection: "column", gap: 8, minWidth: 0}}>
        <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
          <span className="ep-caption" style={{color: "var(--ep-fg-muted)"}}>Escrow #{e.escrowNumber || "—"}</span>
          <StatusPill status={e.transferStatus}/>
          <ConfidencePill confidence={e.bmMatch.confidence} pct={e.bmMatch.pct}/>
          {e.newBuild && <span className="ep-badge sunset">New Build</span>}
          {e.closingSoon && <span className="ep-badge" style={{background:"rgba(255,157,58,0.18)",color:"var(--ep-high-noon)",border:"1px solid var(--ep-high-noon)"}}>Closing Soon</span>}
          {e.mlsDiscrepancy && <span className="ep-badge" style={{background:"rgba(180,30,30,0.12)",color:"var(--ep-rosewood)",border:"1px solid var(--ep-rosewood)"}}>MLS Mismatch</span>}
        </div>
        <div style={{font: "300 24px/1.1 var(--ep-font-display)", letterSpacing: "-0.01em"}}>{e.address}</div>
        <div style={{display: "flex", gap: 16, color: "var(--ep-fg-muted)", fontSize: 13, flexWrap: "wrap"}}>
          <span>{e.city}</span>
          <span style={{fontWeight: 600, color: "var(--ep-origin)"}}>{e.price}</span>
          <span style={{display: "flex", alignItems: "center", gap: 4}}><IcKey size={13}/> {e.escrowCompany}</span>
          <span style={{display: "flex", alignItems: "center", gap: 4}}><IcClock size={13}/> Close {e.closeDate}</span>
        </div>
        <div style={{fontSize: 13, color: "var(--ep-fg-muted)", marginTop: 4}}>
          Representing <strong style={{color: "var(--ep-origin)", fontWeight: 600, textTransform: "capitalize"}}>{e.represents}</strong>
          {" · "}Buyer: <strong style={{color: "var(--ep-origin)", fontWeight: 600}}>{e.buyer.name || "—"}</strong>
          {" · "}Seller: <strong style={{color: "var(--ep-origin)", fontWeight: 600}}>{e.seller.name || "—"}</strong>
          {(!e.buyer.email || !e.seller.email || !e.escrowNumber) && e.transferStatus === "draft" &&
            <span style={{color: "var(--ep-rosewood)", marginLeft: 6}}>· Needs review</span>}
        </div>
        {e.transferError && (
          <div style={{fontSize: 13, marginTop: 6, padding: "8px 12px", background: "rgba(180,30,30,0.08)", border: "1px solid rgba(180,30,30,0.35)", borderRadius: 2, color: "var(--ep-rosewood)", fontWeight: 500}}>
            Transfer failed — {e.transferError}
          </div>
        )}
        {e.mlsDiscrepancy && (
          <div style={{fontSize: 12, marginTop: 6, padding: "6px 10px", background: "rgba(180,30,30,0.08)", border: "1px solid rgba(180,30,30,0.25)", borderRadius: 2, color: "var(--ep-rosewood)"}}>
            <strong>MLS discrepancy:</strong> {e.mlsDiscrepancy.note}
            {e.mlsDiscrepancy.mlsMls && <span style={{color:"var(--ep-fg-muted)",marginLeft:8}}>MLS# {e.mlsDiscrepancy.mlsMls}</span>}
          </div>
        )}
        {e.closingSoon && (
          <div style={{fontSize: 12, marginTop: 6, padding: "6px 10px", background: "rgba(255,157,58,0.10)", border: "1px solid rgba(255,157,58,0.35)", borderRadius: 2, color: "var(--ep-high-noon)"}}>
            <strong>Closing soon</strong> — verify with your broker whether this transfer is still needed.
          </div>
        )}
      </div>
      <div style={{display: "flex", flexDirection: "column", gap: 8, justifyContent: "center"}}>
        <button className="ep-btn secondary sm" onClick={onEdit}><IcEdit/> Review & Send</button>
      </div>
    </div>
  );
}

function EscrowDetailModal({ escrow, agent, onClose, onSave, onSend }) {
  const [e, setE] = _e_uS({
    ...escrow,
    buyer2:  escrow.buyer2  || { name: "", email: "", phone: "" },
    seller2: escrow.seller2 || { name: "", email: "", phone: "" },
  });
  const [represents, setRepresents] = _e_uS(
    ["buyer","seller"].includes(escrow.represents) ? escrow.represents : "buyer"
  );
  const [showParty2, setShowParty2] = _e_uS(
    !!(escrow.buyer2?.name || escrow.seller2?.name)
  );
  const updBuyer   = (patch) => setE({...e, buyer:   {...e.buyer,   ...patch}});
  const updSeller  = (patch) => setE({...e, seller:  {...e.seller,  ...patch}});
  const updBuyer2  = (patch) => setE({...e, buyer2:  {...e.buyer2,  ...patch}});
  const updSeller2 = (patch) => setE({...e, seller2: {...e.seller2, ...patch}});

  const party  = represents === "buyer" ? e.buyer : e.seller;
  const party2 = represents === "buyer" ? e.buyer2 : e.seller2;
  const hasParty2 = showParty2 && !!(party2?.name?.trim() && party2?.email?.trim());
  const canSend = party.name && party.email && e.escrowNumber;

  return (
    <div className="modal-backdrop" onClick={(ev) => ev.target.classList.contains("modal-backdrop") && onClose()}>
      <div style={{width: "min(980px, 95vw)", maxHeight: "92vh", background: "var(--ep-bg-elevated)", borderRadius: 4, display: "flex", flexDirection: "column", animation: "slideUp 260ms var(--ep-ease)"}}>
        {/* Header */}
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 28px", borderBottom: "1px solid var(--ep-border)"}}>
          <div>
            <div className="ep-caption" style={{color: "var(--ep-sunset)"}}>Open Escrow Transfer Authorization</div>
            <div style={{font: "300 24px/1.1 var(--ep-font-display)", marginTop: 4}}>{e.address}</div>
          </div>
          <div style={{display: "flex", alignItems: "center", gap: 12}}>
            <div style={{display: "flex", alignItems: "center", gap: 8, fontSize: 13}}>
              <span style={{color: "var(--ep-fg-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", fontSize: 11}}>Represents</span>
              {["buyer","seller"].map(side => (
                <button key={side} className={`ep-btn ${represents === side ? "primary" : "ghost"} sm`}
                  style={{minWidth: 70, textTransform: "capitalize"}}
                  onClick={() => setRepresents(side)}>{side}</button>
              ))}
            </div>
            <button className="ep-btn ghost sm" onClick={onClose}><IcX/></button>
          </div>
        </div>

        {/* Body */}
        <div className="scroll-y ep-stack-mobile" style={{padding: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28}}>
          {/* Left: BoldTrail data */}
          <div>
            <div className="ep-eyebrow" style={{color: "var(--ep-down-to-earth)"}}>Pulled from BoldTrail BackOffice</div>
            <div style={{padding: 20, background: "rgba(204,207,129,0.18)", border: "1px solid var(--ep-avocado)", borderRadius: 2, marginTop: 8}}>
              <EDataRow label="Transaction" value={`#${e.bmId}`}/>
              <EDataRow label="Property" value={e.address}/>
              <EDataRow label="City / ZIP" value={e.city}/>
              <EDataRow label="Sale Price" value={e.price}/>
              <EDataRow label="Representing" value={e.represents} cap/>
              <EDataRow label="Escrow Co." value={e.escrowCompany}/>
              <EDataRow label="Accepted" value={e.acceptance}/>
              <EDataRow label="Est. Close" value={e.closeDate}/>
              <EDataRow label="Current Broker" value={MHG} last/>
            </div>
            <div style={{fontSize: 11, color: "var(--ep-fg-muted)", marginTop: 10, letterSpacing: "0.05em", textTransform: "uppercase"}}>
              Read-only · Synced from Brokermint
            </div>

            {/* Routing */}
            <div style={{marginTop: 20}}>
              <div className="ep-eyebrow">Signature Routing</div>
              <div style={{padding: 16, background: "#FFFAF0", border: "1px solid var(--ep-border)", borderRadius: 2, fontSize: 13, color: "var(--ep-fg)"}}>
                <div style={{marginBottom: 8}}><strong>1.</strong> {agent.name} <span style={{color:"var(--ep-fg-muted)"}}>(Team Member — reviewer)</span></div>
                {represents === "buyer"
                  ? <div style={{marginBottom: 8}}><strong>2.</strong> {e.buyer.name || "Buyer 1"} <span style={{color:"var(--ep-fg-muted)"}}>(Buyer 1)</span></div>
                  : <div style={{marginBottom: 8}}><strong>2.</strong> {e.newBuild ? `${e.seller.name} (Builder, as Seller)` : (e.seller.name || "Seller 1")} <span style={{color:"var(--ep-fg-muted)"}}>(Seller 1)</span></div>
                }
                {hasParty2 && (
                  <div style={{marginBottom: 8}}><strong>3.</strong> {party2.name} <span style={{color:"var(--ep-fg-muted)"}}>{represents === "buyer" ? "(Buyer 2)" : "(Seller 2)"}</span></div>
                )}
                <div style={{marginBottom: 8}}><strong>{hasParty2 ? 4 : 3}.</strong> Amber Valentine · {RECEIVING_BROKER} <span style={{color:"var(--ep-fg-muted)"}}>(Receiving Broker — auto-sign)</span></div>
                {e.transactionOwnerEmail && (
                  <div style={{paddingTop: 8, borderTop: "1px solid var(--ep-divider)", color: "var(--ep-fg-muted)", display: "flex", alignItems: "center", gap: 6}}>
                    <IcMail size={13}/> TC copy: {e.transactionOwnerEmail}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: editable party + fee */}
          <div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8}}>
              <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>
                {represents === "buyer" ? "Buyer" : "Seller"} — Confirm before sending
              </div>
              <ConfidencePill confidence={e.bmMatch.confidence} pct={e.bmMatch.pct}/>
            </div>
            {e.bmMatch.confidence !== "high" && (
              <div style={{padding: "10px 14px", background: "rgba(255,157,58,0.15)", border: "1px solid var(--ep-high-noon)", borderRadius: 2, fontSize: 12, color: "var(--ep-origin)", marginBottom: 16}}>
                {e.bmMatch.confidence === "low"
                  ? "Low-confidence sync. Verify the contact details carefully."
                  : "Medium-confidence sync. Confirm contact details before sending."}
              </div>
            )}

            {represents === "buyer" ? (
              <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                <label className="ep-field" style={{gridColumn: "1 / -1", marginBottom: 4}}>
                  <span className="ep-eyebrow">Buyer 1</span>
                  <input className="ep-input" value={e.buyer.name} onChange={ev => updBuyer({name: ev.target.value})}/>
                </label>
                <label className="ep-field"><span className="ep-eyebrow">Buyer 1 Email</span>
                  <input className="ep-input" value={e.buyer.email} onChange={ev => updBuyer({email: ev.target.value})} placeholder="buyer@email.com"/></label>
                <label className="ep-field"><span className="ep-eyebrow">Buyer 1 Phone</span>
                  <input className="ep-input" value={e.buyer.phone} onChange={ev => updBuyer({phone: ev.target.value})}/></label>
              </div>
            ) : (
              <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                <label className="ep-field" style={{gridColumn: "1 / -1", marginBottom: 4}}>
                  <span className="ep-eyebrow">Seller 1{e.newBuild && " (Builder)"}</span>
                  <input className="ep-input" value={e.seller.name} onChange={ev => updSeller({name: ev.target.value})}/>
                </label>
                <label className="ep-field"><span className="ep-eyebrow">Seller 1 Email</span>
                  <input className="ep-input" value={e.seller.email} onChange={ev => updSeller({email: ev.target.value})} placeholder="seller@email.com"/></label>
                <label className="ep-field"><span className="ep-eyebrow">Seller 1 Phone</span>
                  <input className="ep-input" value={e.seller.phone} onChange={ev => updSeller({phone: ev.target.value})}/></label>
              </div>
            )}

            {/* Optional second party */}
            <div style={{marginTop: 12}}>
              {!showParty2 ? (
                <button className="ep-btn ghost sm" style={{fontSize: 12}} onClick={() => setShowParty2(true)}>
                  + Add a second {represents === "buyer" ? "buyer" : "seller"}
                </button>
              ) : (
                <div style={{border: "1px solid var(--ep-border)", borderRadius: 2, padding: "14px 16px", marginTop: 4}}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10}}>
                    <span className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>
                      {represents === "buyer" ? "Buyer 2" : "Seller 2"} (optional)
                    </span>
                    <button className="ep-btn ghost sm" style={{fontSize: 11, padding: "2px 8px"}}
                      onClick={() => { setShowParty2(false); represents === "buyer" ? updBuyer2({name:"",email:"",phone:""}) : updSeller2({name:"",email:"",phone:""}); }}>
                      Remove
                    </button>
                  </div>
                  {represents === "buyer" ? (
                    <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                      <label className="ep-field" style={{gridColumn: "1 / -1", marginBottom: 4}}>
                        <span className="ep-eyebrow">Buyer 2 Name</span>
                        <input className="ep-input" value={e.buyer2.name} onChange={ev => updBuyer2({name: ev.target.value})} placeholder="Full name"/>
                      </label>
                      <label className="ep-field"><span className="ep-eyebrow">Buyer 2 Email</span>
                        <input className="ep-input" value={e.buyer2.email} onChange={ev => updBuyer2({email: ev.target.value})} placeholder="Required to send"/></label>
                      <label className="ep-field"><span className="ep-eyebrow">Buyer 2 Phone</span>
                        <input className="ep-input" value={e.buyer2.phone} onChange={ev => updBuyer2({phone: ev.target.value})}/></label>
                    </div>
                  ) : (
                    <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
                      <label className="ep-field" style={{gridColumn: "1 / -1", marginBottom: 4}}>
                        <span className="ep-eyebrow">Seller 2 Name</span>
                        <input className="ep-input" value={e.seller2.name} onChange={ev => updSeller2({name: ev.target.value})} placeholder="Full name"/>
                      </label>
                      <label className="ep-field"><span className="ep-eyebrow">Seller 2 Email</span>
                        <input className="ep-input" value={e.seller2.email} onChange={ev => updSeller2({email: ev.target.value})} placeholder="Required to send"/></label>
                      <label className="ep-field"><span className="ep-eyebrow">Seller 2 Phone</span>
                        <input className="ep-input" value={e.seller2.phone} onChange={ev => updSeller2({phone: ev.target.value})}/></label>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{marginTop: 4}}>
              <label className="ep-field" style={{marginBottom: 0}}>
                <span className="ep-eyebrow">Escrow Number</span>
                <input className="ep-input" value={e.escrowNumber} onChange={ev => setE({...e, escrowNumber: ev.target.value})} placeholder="Required"/>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{display: "flex", justifyContent: "space-between", padding: "18px 28px", borderTop: "1px solid var(--ep-border)", background: "#FFFAF0"}}>
          <button className="ep-btn ghost" onClick={onClose}>Cancel</button>
          <div className="hstack">
            <button className="ep-btn secondary sm" onClick={() => onSave({...e, represents})}>Save as Draft</button>
            <button className="ep-btn primary sm" disabled={!canSend}
              onClick={() => onSend({
                ...e,
                represents,
                buyer2:  (represents === "buyer"  && hasParty2) ? e.buyer2  : null,
                seller2: (represents === "seller" && hasParty2) ? e.seller2 : null,
              })}>
              <IcSend/> Send {represents === "buyer" ? "Buyer" : "Seller"} Transfer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BoldTrailEmailResolver({ onResolved }) {
  const [email, setEmail] = _e_uS("");
  const [status, setStatus] = _e_uS("idle"); // idle | searching | found | not_found | error
  const [foundName, setFoundName] = _e_uS("");
  const inputRef = _e_uR(null);

  async function search(e) {
    e.preventDefault();
    const val = email.trim();
    if (!val) return;
    setStatus("searching");
    try {
      const r = await fetch("/api/v1/escrows/resolve-boldtrail-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: val }),
      });
      const data = await r.json();
      if (r.ok && data.ok) {
        setFoundName(data.name || val);
        setStatus("found");
        setTimeout(() => onResolved?.(), 800);
      } else if (r.status === 404) {
        setStatus("not_found");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <div style={{marginBottom: 28, padding: "20px 24px", background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", borderRadius: 2}}>
      <div style={{display: "flex", alignItems: "flex-start", gap: 14}}>
        <IcSearch size={18} style={{marginTop: 2, flexShrink: 0, color: "var(--ep-fg-muted)"}}/>
        <div style={{flex: 1}}>
          <div style={{fontWeight: 600, fontSize: 14, marginBottom: 4}}>No escrows found in BoldTrail</div>
          <div style={{fontSize: 13, color: "var(--ep-fg-muted)", marginBottom: 14}}>
            If your BoldTrail BackOffice login email is different from the one you used here, enter it below to connect your account.
          </div>
          {status === "found" ? (
            <div style={{fontSize: 13, color: "var(--ep-down-to-earth)", display: "flex", alignItems: "center", gap: 8}}>
              <span style={{fontSize: 16}}>✓</span> Connected as <strong>{foundName}</strong> — reloading your escrows…
            </div>
          ) : (
            <form onSubmit={search} style={{display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap"}}>
              <input
                ref={inputRef}
                className="ep-input"
                style={{flex: "1 1 240px", minWidth: 0}}
                type="email"
                placeholder="your@boldtrail-email.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setStatus("idle"); }}
                disabled={status === "searching"}
              />
              <button className="ep-btn secondary sm" type="submit" disabled={!email.trim() || status === "searching"}>
                {status === "searching" ? "Searching…" : "Find my account"}
              </button>
              {status === "not_found" && (
                <span style={{fontSize: 12, color: "var(--ep-rosewood)", width: "100%"}}>
                  No BoldTrail user found for that email. Try another address or contact support.
                </span>
              )}
              {status === "error" && (
                <span style={{fontSize: 12, color: "var(--ep-rosewood)", width: "100%"}}>
                  Something went wrong — try again or contact support.
                </span>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function EDataRow({ label, value, last, cap }) {
  return (
    <div style={{display: "grid", gridTemplateColumns: "112px 1fr", gap: 16, padding: "8px 0", borderBottom: last ? 0 : "1px solid rgba(71,60,22,0.1)", fontSize: 13}}>
      <div style={{color: "var(--ep-fg-muted)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600}}>{label}</div>
      <div style={{color: "var(--ep-origin)", textTransform: cap ? "capitalize" : "none"}}>{value || "—"}</div>
    </div>
  );
}

export { StepEscrow };
